import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useState } from "react";
import Alert from "components/common/Alert";
import Button from "components/common/Button";
import { LoadingButton } from "components/common/Loading";
import { currentUrlWithoutQueryString } from "helpers/urls";
import styles from "./StripeCheckoutForm.module.css";

interface Props {
  value: number;
}

export const StripeCheckoutForm = ({ value }: Props) => {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [storedValue, setStoredValue] = useState(0);

  useEffect(() => {
    if (storedValue && value !== storedValue) {
      setStoredValue(value);
      elements?.fetchUpdates();
    }
  }, [value, storedValue, elements]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: currentUrlWithoutQueryString(),
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message || "Unspecified card or validation error");
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <Button
        disabled={isLoading || !stripe || !elements}
        id="submit"
        type="submit"
        theme="primary"
        className={styles.submit}
      >
        <span id="button-text">{isLoading ? <LoadingButton /> : "Pay now"}</span>
      </Button>
      {/* Show any error or success messages */}
      {message && <Alert theme="danger">{message}</Alert>}
    </form>
  );
};
